import React, { useState, useEffect } from "react";
import { useNavigate, useLocation, Link } from "react-router-dom";
import { Menu, X, ArrowRight, Coins } from "lucide-react";
import useUser from "../hooks/useUser";
import { clearToken } from "../utils/auth";

const Navbar = ({ isLoggedIn = false, isLandingPage = false }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, loading } = useUser();

  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [loggedIn, setLoggedIn] = useState(!!user);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setLoggedIn(!!user);
  }, [user]);

  useEffect(() => {
    if (location.pathname !== "/" || !location.hash) return;

    const id = location.hash.slice(1);
    const timer = setTimeout(() => {
      document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });
    }, 150);

    return () => clearTimeout(timer);
  }, [location]);

  if (loading) return null;

  const handleSignout = () => {
    clearToken();
    setLoggedIn(false);
    navigate("/");
  };

  const linkClass = "text-slate-300 hover:text-white transition text-sm font-medium";

  return (
    <>
      {!isLandingPage && <div className="h-16" />}

      <nav
        className={`fixed top-0 left-0 right-0 z-50 transition-all ${!isLandingPage
          ? "bg-[#020617] border-b border-white/10"
          : scrolled
            ? "bg-[#020617]/90 backdrop-blur-md border-b border-white/10"
            : "bg-transparent"
          }`}
      >
        <div className="max-w-7xl mx-auto px-6 h-20 flex items-center justify-between">

          {/* Logo */}
          <div
            onClick={() => navigate("/")}
            className="flex items-center gap-2 cursor-pointer w-48"
          >
            <img
              src={process.env.PUBLIC_URL + "/images/tradeye.png"}
              alt="Tradeye"
              className="h-8 w-auto object-contain"
            />
          </div>

          {/* Desktop Center Links */}
          <div className="hidden md:flex items-center gap-8">
            <Link to="/#how-it-works" className={linkClass}>How It Works</Link>
            <Link to="/pricing" className={linkClass}>Pricing</Link>
            <Link to={isLoggedIn ? "/select-broker" : "/login"} className={linkClass}>Analyze</Link>
            <Link to="/#contact" className={linkClass}>Contact</Link>
            {!loggedIn ? (
              <Link to="/login" className={linkClass}>Login</Link>
            ) : (
              <>
                <Link to="/history" className={linkClass}>History</Link>
                <Link to="/analytics" className={linkClass}>Analytics</Link>
                <button onClick={handleSignout} className={linkClass}>Logout</button>
              </>
            )}
          </div>

          {/* Desktop Right */}
          <div className="hidden md:flex items-center justify-end gap-5">
            {loggedIn && (
              <div
                className="flex items-center gap-1.5 text-sm font-medium text-slate-300"
                title={user?.has_unlimited_coins ? "Unlimited coins" : `${user?.coins ?? 0} coins remaining`}
              >
                <Coins className="h-4 w-4 text-yellow-400" />
                <span>
                  {user?.has_unlimited_coins ? "∞" : user?.coins ?? 0}
                </span>
              </div>
            )}

            <button
              onClick={() => navigate(loggedIn ? "/select-broker" : "/signup")}
              className="flex items-center gap-2 rounded-full bg-[#0052FF] px-5 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-blue-600"
            >
              Start Free
              <ArrowRight className="h-4 w-4" />
            </button>
          </div>

          {/* Mobile toggle */}
          <div className="md:hidden flex items-center gap-4">
            {loggedIn && (
              <div
                className="flex items-center gap-1.5 text-sm font-medium text-slate-300"
                title={user?.has_unlimited_coins ? "Unlimited coins" : `${user?.coins ?? 0} coins`}
              >
                <Coins className="h-4 w-4 text-yellow-400" />
                <span>
                  {user?.has_unlimited_coins ? "∞" : user?.coins ?? 0}
                </span>
              </div>
            )}

            <button
              onClick={() => setOpen(!open)}
              className="text-slate-300"
            >
              {open ? <X /> : <Menu />}
            </button>
          </div>
        </div>

        {/* Mobile menu */}
        {open && (
          <div className="md:hidden bg-[#020617] border-t border-white/10 px-6 py-4 flex flex-col gap-4">
            <Link to="/#how-it-works" className={linkClass} onClick={() => setOpen(false)}>How It Works</Link>
            <Link to="/pricing" className={linkClass} onClick={() => setOpen(false)}>Pricing</Link>
            <Link to="/select-broker" className={linkClass} onClick={() => setOpen(false)}>Analyze</Link>
            <Link to="/#contact" className={linkClass} onClick={() => setOpen(false)}>Contact</Link>

            {!loggedIn ? (
              <Link to="/login" className={linkClass} onClick={() => setOpen(false)}>Login</Link>
            ) : (
              <button onClick={() => { handleSignout(); setOpen(false); }} className="text-left text-slate-300">Logout</button>
            )}

            <button
              onClick={() => { navigate("/signup"); setOpen(false); }}
              className="w-full bg-[#0052FF] py-3 rounded-full text-white font-semibold flex justify-center items-center gap-2 mt-2"
            >
              Start Free <ArrowRight className="w-4 h-4" />
            </button>
          </div>
        )}
      </nav>
    </>
  );
};

export default Navbar;